/**
 * Route classification for the lazy Stytch provider boundary (StytchProviderGate
 * in App.tsx).
 *
 * Pure marketing/content routes never render a component that calls a Stytch hook
 * (useStytch / useStytchUser / useStytchMemberSession), so they must stay on the
 * no-SDK path — this is what keeps the Stytch auth SDK off the critical path for
 * first load on the public site. Everything else (auth routes, the authenticated
 * app, and /jobs/:slug — whose apply wizard SignupStep calls useStytch()
 * unconditionally) requires the provider boundary.
 *
 * NOTE: this is intentionally NOT the same as Router's `isPublicRoute`. That
 * predicate means "no login required to view" and includes auth routes like
 * /candidate-auth — which are public to VISIT but DO need the Stytch SDK. Here we
 * answer the narrower question "does this route need the Stytch SDK loaded?".
 */

// Marketing/content routes matched exactly (after normalization).
export const MARKETING_EXACT_ROUTES = new Set<string>([
  "/",
  "/candidates",
  "/company/about",
  "/company/contact",
  "/company/pricing",
  "/company/pricing/hiring-app",
  "/company/pricing/ats-layer",
  "/connect-your-ats",
  "/sample-scorecard",
  "/what-you-get",
  "/how-it-works",
  "/jobs",
]);

// Marketing/content route prefixes (sub-paths are also marketing).
export const MARKETING_PREFIX_ROUTES = [
  // The 12 feature landing pages: marketing copy plus demos that run on
  // fixtures. Their CTAs are links to the auth routes, so nothing here calls a
  // Stytch hook.
  "/features/",
  "/legal/",
  "/cv-vs-market",
  "/gdpr",
  "/schedule/",
];

/**
 * Returns true when the given location requires the Stytch SDK / provider boundary.
 *
 * The incoming location (from wouter) can carry a query string or hash
 * (e.g. "/jobs?page=2", "/jobs#top") and links may include a trailing slash, so we
 * normalize before allowlist matching — mirroring Router's own /jobs listing
 * handling so the public job listing never trips the gate.
 */
export function pathNeedsStytch(rawPath: string): boolean {
  let path = rawPath.split(/[?#]/)[0] ?? rawPath;
  if (path.length > 1 && path.endsWith("/")) {
    path = path.slice(0, -1);
  }
  if (MARKETING_EXACT_ROUTES.has(path)) return false;
  if (MARKETING_PREFIX_ROUTES.some((prefix) => path.startsWith(prefix))) return false;
  return true;
}
